import React, { useEffect, useMemo, useRef } from 'react';
import {
	Alert,
	Box,
	Button,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Stack,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import type {
	Control,
	FieldErrors,
	UseFormClearErrors,
	UseFormGetValues,
	UseFormResetField,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { es } from 'date-fns/locale';
import { format, isValid as isValidDate } from 'date-fns';

import type {
	IServiceProviderAutomaticPaymentDateInfoDTO,
	IServiceProviderManualPaymentDateLineDTO,
} from '../../../../../../features/salesOrder/types';
import { formatDateToShowUser } from '../../../../../../features/common/helpers';

export type ProviderPaymentDateSectionProps = {
	hydrateKey: string;
	control: Control<any>;
	watch: UseFormWatch<any>;
	setValue: UseFormSetValue<any>;
	getValues: UseFormGetValues<any>;
	errors: FieldErrors<any>;
	clearErrors?: UseFormClearErrors<any>;
	resetField?: UseFormResetField<any>;
	servicePath: string; // '' o 'services.0'
	providerIdPath: string; // 'tariffProvider'

	providerAutomaticPaymentDateLine?: IServiceProviderAutomaticPaymentDateInfoDTO | null;
	disabled?: boolean;
};

// ---------- helpers fechas (string yyyy-MM-dd <-> Date local) ----------
const ymdToLocalDate = (value?: string | null): Date | null => {
	if (!value) return null;
	const [yy, mm, dd] = value.split('-').map(Number);
	if (!yy || !mm || !dd) return null;
	return new Date(yy, mm - 1, dd, 12, 0, 0, 0);
};

const localDateToYmd = (date: Date | null): string => {
	if (!date) return '';
	if (!isValidDate(date)) return '';
	const d = new Date(date);
	d.setHours(12, 0, 0, 0);
	return format(d, 'yyyy-MM-dd');
};

const toNumberOrNull = (raw: string): number | null => {
	const t = raw.trim();
	if (t === '') return null;
	const n = Number(t);
	if (!Number.isFinite(n)) return null;
	return n;
};

const getErrorMessage = (errors: any, path: string): string | undefined => {
	const parts = path.split('.');
	let cur: any = errors;
	for (const p of parts) {
		if (cur == null) return undefined;
		cur = cur[p];
	}
	return cur?.message as string | undefined;
};

const hasError = (errors: any, path: string): boolean => Boolean(getErrorMessage(errors, path));

// ---------- UI ----------
type StatusBoxProps = {
	icon: React.ReactNode;
	title: string;
	description?: string;
	severity: 'success' | 'info' | 'warning' | 'error';
};

const StatusBox: React.FC<StatusBoxProps> = ({ icon, title, description, severity }: StatusBoxProps) => (
	<Alert icon={icon} severity={severity} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
		<Box display="flex" flexDirection="column" gap={0.5}>
			<Typography variant="subtitle2">{title}</Typography>
			{description ? <Typography variant="body2">{description}</Typography> : null}
		</Box>
	</Alert>
);

const normalizeLine = (l: any): IServiceProviderManualPaymentDateLineDTO => ({
	id: l?.id ?? null,
	paymentDate: l?.paymentDate ?? '',
	amount: l?.amount ?? null,
	observation: l?.observation ?? '',
});

const newEmptyLine = (): IServiceProviderManualPaymentDateLineDTO => ({
	id: null,
	paymentDate: '',
	amount: null,
	observation: '',
});

export const ProviderPaymentDateSection: React.FC<ProviderPaymentDateSectionProps> = ({
	hydrateKey,
	control,
	watch,
	setValue,
	getValues,
	errors,
	clearErrors,
	resetField,
	servicePath,
	providerIdPath,
	providerAutomaticPaymentDateLine,
	disabled,
}: ProviderPaymentDateSectionProps) => {
	const automaticPath = servicePath
		? `${servicePath}.automaticProviderPaymentDateCalculation`
		: 'automaticProviderPaymentDateCalculation';

	const manualLinesPath = servicePath
		? `${servicePath}.providerManualPaymentDateLines`
		: 'providerManualPaymentDateLines';

	// provider (tu form usa tariffProvider)
	const providerRaw = watch(providerIdPath) as any;
	const providerId = useMemo(() => {
		if (!providerRaw) return null;

		// si viene string
		if (typeof providerRaw === 'string') {
			const v = providerRaw.trim();
			if (v === 'n/a') return null;
			return v.length > 0 ? v : null;
		}

		// si viene objeto
		if (typeof providerRaw === 'object') {
			const candidate = providerRaw.id ?? providerRaw.value ?? providerRaw.providerId ?? null;
			if (candidate == null) return null;

			// si candidate es string vacío => null
			if (typeof candidate === 'string') {
				const v = candidate.trim();
				return v.length > 0 ? v : null;
			}

			// si candidate es number (por las dudas)
			if (typeof candidate === 'number') {
				return Number.isFinite(candidate) ? String(candidate) : null;
			}

			return null;
		}

		return null;
	}, [providerRaw]);

	// ✅ ÚNICA FUENTE de "automatic"
	const automaticRaw = watch(automaticPath as any);
	const hasProvider = Boolean(providerId);
	const automatic = hasProvider ? automaticRaw !== false : true;

	// ----- si cambia provider y estabas manual => forzar auto + limpiar -----
	const prevProviderIdRef = useRef<string | null>(null);

	useEffect(() => {
		const prev = prevProviderIdRef.current;
		const next = providerId;

		if (prev === null) {
			prevProviderIdRef.current = next ?? null;
			return;
		}

		if (!next || next === prev) return;

		const isAutoNow = getValues(automaticPath as any) !== false;

		if (!isAutoNow) {
			if (resetField) {
				resetField(automaticPath as any, { defaultValue: true });
				resetField(manualLinesPath as any, { defaultValue: [] });
			} else {
				setValue(automaticPath as any, true, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
				setValue(manualLinesPath as any, [], { shouldDirty: false, shouldTouch: false, shouldValidate: false });
			}

			if (clearErrors) clearErrors([automaticPath as any, manualLinesPath as any] as any);
		}

		prevProviderIdRef.current = next;
	}, [providerId, automaticPath, manualLinesPath, getValues, setValue, clearErrors, resetField]);

	// ----- sin provider => siempre automático + líneas vacías -----
	useEffect(() => {
		if (!providerId) {
			const cur = getValues(automaticPath as any);
			if (cur !== true) {
				setValue(automaticPath as any, true, {
					shouldDirty: false,
					shouldTouch: false,
					shouldValidate: false,
				});
			}

			setValue(manualLinesPath as any, [], {
				shouldDirty: false,
				shouldTouch: false,
				shouldValidate: false,
			});

			if (clearErrors) clearErrors(manualLinesPath as any);
		}
	}, [providerId, automaticPath, manualLinesPath, getValues, setValue, clearErrors]);

	// ----- si hay provider y todavía no está seteado => default automático -----
	useEffect(() => {
		if (!providerId) return;

		const cur = getValues(automaticPath as any);
		if (cur === undefined || cur === null) {
			setValue(automaticPath as any, true, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
			setValue(manualLinesPath as any, [], { shouldDirty: false, shouldTouch: false, shouldValidate: false });
			if (clearErrors) clearErrors(manualLinesPath as any);
		}
	}, [providerId, automaticPath, manualLinesPath, getValues, setValue, clearErrors]);

	// Lines desde RHF (fuente de verdad)
	const manualLines = useWatch({
		control,
		name: manualLinesPath as any,
	}) as IServiceProviderManualPaymentDateLineDTO[] | undefined;
	const linesToRender = Array.isArray(manualLines) ? manualLines.map(normalizeLine) : [];

	// Asegurar que el array exista SIEMPRE (backend lo exige)
	useEffect(() => {
		const cur = getValues(manualLinesPath as any);
		if (!Array.isArray(cur)) {
			setValue(manualLinesPath as any, [], { shouldDirty: false, shouldTouch: false, shouldValidate: false });
		}
	}, [manualLinesPath, getValues, setValue]);

	// Si entra a manual y no hay líneas, agrega una (solo si HAY provider)
	const didAutoAddRef = useRef(false);
	useEffect(() => {
		if (!providerId) {
			didAutoAddRef.current = false;
			return;
		}

		if (automatic) {
			didAutoAddRef.current = false;
			return;
		}

		if (!didAutoAddRef.current && linesToRender.length === 0) {
			didAutoAddRef.current = true;
			setValue(manualLinesPath as any, [newEmptyLine()], {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: false,
			});
		}
	}, [providerId, automatic, linesToRender.length, manualLinesPath, setValue]);

	// CRUD líneas
	const setLines = (next: IServiceProviderManualPaymentDateLineDTO[]) => {
		setValue(manualLinesPath as any, next.map(normalizeLine), {
			shouldDirty: true,
			shouldTouch: true,
			shouldValidate: false,
		});
	};

	const addLine = () => setLines([...linesToRender, newEmptyLine()]);

	const removeLine = (idx: number) => {
		if (linesToRender.length <= 1) return;
		setLines(linesToRender.filter((_, i) => i !== idx));
	};

	// Status automático
	const autoStatus = useMemo(() => {
		const info = providerAutomaticPaymentDateLine;

		if (!info) {
			return (
				<StatusBox
					severity="info"
					icon={<InfoOutlinedIcon />}
					title="Cálculo automático"
					description="Todavía no hay información calculada."
				/>
			);
		}

		if (info.errorMessage) {
			return (
				<StatusBox
					severity="error"
					icon={<ErrorOutlineIcon />}
					title="Error calculando fecha de pago"
					description={info.errorMessage}
				/>
			);
		}

		if (info.notCalculatedReason) {
			return (
				<StatusBox
					severity="warning"
					icon={<ReportProblemOutlinedIcon />}
					title="No se calculó la fecha de pago"
					description={info.notCalculatedReason}
				/>
			);
		}

		if (info.paymentDate || info.amount != null || info.sourceDate) {
			const pieces: string[] = [];
			if (info.paymentDate) pieces.push(`Fecha de pago: ${formatDateToShowUser(info.paymentDate)}`);
			if (info.amount != null) pieces.push(`Monto: ${info.amount} USD`);
			return (
				<StatusBox
					severity="success"
					icon={<CheckCircleOutlineIcon />}
					title="Fecha de pago calculada"
					description={pieces.join(' · ')}
				/>
			);
		}

		return (
			<StatusBox
				severity="info"
				icon={<InfoOutlinedIcon />}
				title="Cálculo automático"
				description="No hay datos suficientes para mostrar."
			/>
		);
	}, [providerAutomaticPaymentDateLine]);

	return (
		<Box
			display="flex"
			flexDirection="column"
			gap={1}
			mt={2}
			mb={2}
			sx={{
				width: '100%',
				maxWidth: '100%',
				boxSizing: 'border-box',
			}}
		>
			<Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
				<Stack direction="row" alignItems="center" spacing={1} mb={1}>
					<InfoOutlinedIcon fontSize="small" color="action" />
					<Typography variant="h6" color="Grey">
						Fecha de pago a proveedor
					</Typography>
				</Stack>

				<Controller
					name={automaticPath as any}
					control={control}
					render={({ field }) => (
						<FormControlLabel
							label={automatic ? 'Automático' : 'Manual'}
							control={
								<Switch
									checked={automatic}
									disabled={disabled || !hasProvider}
									onChange={(_, checked) => {
										if (!hasProvider) return;

										// guardamos true/false en el form
										field.onChange(checked);

										if (checked) {
											setLines([]);
											if (clearErrors) clearErrors(manualLinesPath as any);
										} else {
											const cur = (getValues(manualLinesPath as any) as any[]) ?? [];
											if (!Array.isArray(cur) || cur.length < 1) setLines([newEmptyLine()]);
										}
									}}
								/>
							}
						/>
					)}
				/>
			</Box>

			{!providerId ? (
				<Alert severity="info">
					Seleccioná un proveedor para calcular la fecha de pago (se calculará al guardar) o configurar líneas manuales.
				</Alert>
			) : (
				<>
					{automatic ? (
						<Box>{autoStatus}</Box>
					) : (
						<Box display="flex" flexDirection="column" gap={2}>
							<Alert severity="warning">Modo manual: definís líneas con fecha, monto y observación opcional.</Alert>

							<Box display="flex" justifyContent="flex-end">
								<Button onClick={addLine} startIcon={<AddIcon />} size="small" variant="outlined" disabled={disabled}>
									Agregar línea
								</Button>
							</Box>

							<LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
								<Stack spacing={2}>
									{linesToRender.map((line, idx) => {
										const datePath = `${manualLinesPath}.${idx}.paymentDate`;
										const amountPath = `${manualLinesPath}.${idx}.amount`;
										const obsPath = `${manualLinesPath}.${idx}.observation`;

										return (
											<Box
												key={`${hydrateKey}-${line.id ?? `idx-${idx}`}`}
												display="grid"
												alignItems="center"
												gap={2}
												sx={{ gridTemplateColumns: { xs: '1fr', md: '220px 180px 1fr 44px' } }}
											>
												<Controller
													name={datePath as any}
													control={control}
													rules={{ required: 'Fecha es requerida' }}
													render={({ field }) => (
														<DatePicker
															label="Fecha de pago"
															inputFormat="dd/MM/yyyy"
															value={ymdToLocalDate(field.value ?? '')}
															onChange={(d) => field.onChange(localDateToYmd(d))}
															renderInput={(params) => (
																<TextField
																	{...params}
																	size="small"
																	fullWidth
																	disabled={disabled}
																	error={hasError(errors, datePath)}
																	helperText={getErrorMessage(errors, datePath)}
																/>
															)}
														/>
													)}
												/>

												<Controller
													name={amountPath as any}
													control={control}
													rules={{
														required: 'Monto es requerido',
														validate: (v: any) => (v != null && Number(v) > 0) || 'Monto debe ser > 0',
													}}
													render={({ field }) => (
														<TextField
															size="small"
															label="Monto"
															fullWidth
															disabled={disabled}
															value={field.value ?? ''}
															onChange={(e) => field.onChange(toNumberOrNull(e.target.value))}
															error={hasError(errors, amountPath)}
															helperText={getErrorMessage(errors, amountPath)}
															InputProps={{
																startAdornment: <InputAdornment position="start">$</InputAdornment>,
															}}
														/>
													)}
												/>

												<Controller
													name={obsPath as any}
													control={control}
													render={({ field }) => (
														<TextField
															size="small"
															label="Observación (opcional)"
															fullWidth
															disabled={disabled}
															value={field.value ?? ''}
															onChange={(e) => field.onChange(e.target.value)}
														/>
													)}
												/>

												<Tooltip title={linesToRender.length <= 1 ? 'Debe existir al menos 1 línea' : 'Eliminar'}>
													<span>
														<IconButton
															size="small"
															disabled={disabled || linesToRender.length <= 1}
															onClick={() => removeLine(idx)}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</span>
												</Tooltip>
											</Box>
										);
									})}
								</Stack>
							</LocalizationProvider>
						</Box>
					)}
				</>
			)}
		</Box>
	);
};
